import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EdFiLogo } from '../components/EdFiLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, Eye, EyeOff, ArrowLeft, GraduationCap, Building2, ShieldCheck } from 'lucide-react';

const DEMO_USERS = [
  {
    label: 'Estudiante',
    icon: GraduationCap,
    email: 'estudiante@test.com',
    password: 'test123',
    color: 'bg-brand-magenta/10 text-brand-magenta border-brand-magenta/20 hover:bg-brand-magenta/20',
  },
  {
    label: 'Universidad',
    icon: Building2,
    email: 'universidad@demo.com',
    password: 'demo123',
    color: 'bg-brand-blue/10 text-brand-blue border-brand-blue/20 hover:bg-brand-blue/20',
  },
  {
    label: 'Admin',
    icon: ShieldCheck,
    email: 'admin@edfi.la',
    password: 'EdFi2026Admin!',
    color: 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200',
  },
];

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Algo salio mal. Intenta de nuevo.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      // Wait for React state propagation before navigating
      await new Promise(resolve => setTimeout(resolve, 50));
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'university') {
        navigate('/university');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoUser = (demoUser) => {
    setEmail(demoUser.email);
    setPassword(demoUser.password);
    setError('');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-brand-blue transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Volver al inicio</span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <div className="text-center mb-8">
              <EdFiLogo size="lg" linkTo="/" />
              <p className="text-slate-600 mt-2">Inicia sesion en tu cuenta</p>
            </div>

            {/* Demo User Buttons */}
            <div className="mb-6">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3 text-center">Acceso rapido demo</p>
              <div className="grid grid-cols-3 gap-2">
                {DEMO_USERS.map((demo) => {
                  const Icon = demo.icon;
                  return (
                    <button
                      key={demo.label}
                      type="button"
                      onClick={() => handleDemoUser(demo)}
                      className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-sm font-medium transition-all ${demo.color}`}
                      data-testid={`demo-${demo.label.toLowerCase()}-btn`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-xs">{demo.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-slate-400 text-xs">o ingresa manualmente</span>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-slate-700 text-sm">Correo electronico</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="pl-10"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-700 text-sm">Contrasena</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="********"
                    className="pl-10 pr-10"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-brand-magenta hover:bg-brand-magenta-dark text-white py-5 font-semibold"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Iniciando sesion...' : 'Iniciar Sesion'}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-400 text-xs">o continua con</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full border-slate-300 hover:bg-slate-50"
              onClick={loginWithGoogle}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar con Google
            </Button>

            <p className="text-center text-slate-600 mt-6 text-sm">
              No tienes cuenta?{' '}
              <Link to="/register" className="text-brand-blue hover:underline font-medium">
                Registrate aqui
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
